import React, { useState } from "react";
import axios from "axios";
import {
  ArrowRight, ArrowLeft, Check, CalendarDays, Clock, User, CheckCircle2, Loader2, Sparkles,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Calendar } from "../components/ui/calendar";
import { useToast } from "../hooks/use-toast";
import { Eyebrow } from "../components/common";
import Seo from "../components/Seo";
import { assessments, timeSlots } from "../mock";

const steps = ["Grade", "Date & Time", "Your Details"];

function fmtDate(d) {
  if (!d) return "";
  return d.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
}

export default function Book() {
  const { toast } = useToast();
  const [step, setStep] = useState(0);
  const [grade, setGrade] = useState(null);
  const [date, setDate] = useState(null);
  const [slot, setSlot] = useState("");
  const [form, setForm] = useState({ parent_name: "", child_name: "", email: "", phone: "", notes: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const next = () => {
    if (step === 0 && !grade) return toast({ title: "Please choose a grade band" });
    if (step === 1 && (!date || !slot)) return toast({ title: "Please pick a date and a time slot" });
    setStep((s) => Math.min(s + 1, 2));
  };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async () => {
    if (!form.parent_name || !form.email || !form.phone) {
      return toast({ title: "Please fill the required fields", description: "Parent name, email and phone are required." });
    }
    setLoading(true);
    try {
      const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
      await axios.post(`${API}/booking`, {
        ...form,
        grade: grade.title,
        assessment: grade.tag,
        date: fmtDate(date),
        slot,
      });
      setDone(true);
      toast({ title: "Booking confirmed", description: "A confirmation email is on its way." });
    } catch (e) {
      toast({ title: "Something went wrong", description: "Please try again in a moment." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="overflow-hidden">
      <Seo
        title="Book a Child Cognitive Assessment | MapMyKid"
        description="Book a MapMyKid cognitive assessment for your child in a few taps. Choose your child's grade, pick a convenient date and time slot, and we will confirm your booking."
      />
      <section className="relative hero-gradient pt-32 sm:pt-40 pb-10">
        <div className="absolute inset-0 grain" />
        <div className="relative mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center animate-fade-up">
          <Eyebrow>Book an Assessment</Eyebrow>
          <h1 className="mt-6 text-[28px] sm:text-[46px] font-extrabold leading-[1.08] text-slate-900">
            Book your child's <span className="brand-gradient-text">assessment</span> in minutes.
          </h1>
          <p className="mt-4 text-[18px] leading-relaxed text-slate-600">
            Choose a grade, pick a time that suits you, and we will take care of the rest.
          </p>
        </div>
      </section>

      <section className="pb-20 bg-white">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          {done ? (
            <div className="rounded-3xl border border-violet-100 bg-white p-8 sm:p-12 text-center shadow-xl shadow-violet-100/50" data-testid="book-success">
              <CheckCircle2 className="mx-auto h-16 w-16 text-teal-500" />
              <h2 className="mt-4 text-[26px] font-extrabold text-slate-900">Thank you, booking confirmed!</h2>
              <p className="mx-auto mt-3 max-w-md text-[17px] text-slate-600">
                Thank you, {form.parent_name.split(" ")[0]}. We have received your request for the{" "}
                <span className="font-semibold text-slate-800">{grade?.title}</span> assessment on{" "}
                <span className="font-semibold text-slate-800">{fmtDate(date)}</span> at{" "}
                <span className="font-semibold text-slate-800">{slot}</span>.
              </p>
              <p className="mx-auto mt-4 max-w-md text-[16px] text-slate-600">
                We have sent a confirmation email to <span className="font-semibold text-slate-800">{form.email}</span> with a secure link to pay your assessment fee online. Our back office team will get in touch with you shortly.
              </p>
            </div>
          ) : (
            <div className="rounded-3xl border border-violet-100 bg-white shadow-xl shadow-violet-100/50 overflow-hidden">
              {/* Stepper */}
              <div className="flex items-center gap-2 sm:gap-4 px-6 sm:px-8 py-5 border-b border-violet-100 bg-violet-50/40">
                {steps.map((label, i) => (
                  <div key={label} className="flex items-center gap-2 sm:gap-3">
                    <span className={`grid h-8 w-8 place-items-center rounded-full text-[15px] font-bold ${i <= step ? "cta-gradient text-white" : "bg-white text-slate-400 border border-violet-100"}`}>
                      {i < step ? <Check className="h-4 w-4" /> : i + 1}
                    </span>
                    <span className={`text-[15px] font-semibold ${i <= step ? "text-slate-900" : "text-slate-400"} hidden sm:block`}>{label}</span>
                    {i < steps.length - 1 && <span className="mx-1 h-px w-5 sm:w-10 bg-violet-200" />}
                  </div>
                ))}
              </div>

              <div className="p-6 sm:p-8">
                {step === 0 && (
                  <div className="space-y-4">
                    <h2 className="text-[20px] font-bold text-slate-900">Which grade is your child in?</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      {assessments.map((a) => {
                        const active = grade?.id === a.id;
                        return (
                          <button
                            key={a.id}
                            onClick={() => setGrade(a)}
                            className={`text-left rounded-2xl border p-5 transition-all ${active ? "border-violet-400 ring-2 ring-violet-200 bg-violet-50/50" : "border-violet-100 hover:border-violet-300"}`}
                          >
                            <span className="flex items-center justify-between">
                              <span className="text-[14px] font-semibold text-violet-600">{a.tag}</span>
                              {active && <Check className="h-5 w-5 text-violet-600" />}
                            </span>
                            <p className="mt-2 text-[18px] font-bold text-slate-900">{a.title}</p>
                            <p className="mt-1 text-[15px] text-slate-500">{a.ages}</p>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {step === 1 && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div>
                      <h2 className="flex items-center gap-2 text-[20px] font-bold text-slate-900"><CalendarDays className="h-5 w-5 text-violet-600" /> Pick a date</h2>
                      <div className="mt-3 rounded-2xl border border-violet-100 p-2 inline-block">
                        <Calendar mode="single" selected={date} onSelect={setDate} disabled={{ before: today }} />
                      </div>
                    </div>
                    <div>
                      <h2 className="flex items-center gap-2 text-[20px] font-bold text-slate-900"><Clock className="h-5 w-5 text-violet-600" /> Choose a slot</h2>
                      <div className="mt-3 grid grid-cols-2 gap-3">
                        {timeSlots.map((s) => (
                          <button
                            key={s}
                            onClick={() => setSlot(s)}
                            className={`rounded-xl border px-4 py-3 text-[16px] font-semibold transition-all ${slot === s ? "cta-gradient text-white border-transparent" : "border-violet-100 text-slate-700 hover:border-violet-300"}`}
                          >
                            {s}
                          </button>
                        ))}
                      </div>
                      {date && slot && (
                        <p className="mt-4 flex items-center gap-2 text-[15px] font-medium text-teal-700"><Sparkles className="h-4 w-4" /> {fmtDate(date)} at {slot}</p>
                      )}
                    </div>
                  </div>
                )}

                {step === 2 && (
                  <div className="space-y-5">
                    <h2 className="flex items-center gap-2 text-[20px] font-bold text-slate-900"><User className="h-5 w-5 text-violet-600" /> Your details</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div className="space-y-2">
                        <Label className="text-[16px]">Parent name *</Label>
                        <Input value={form.parent_name} onChange={update("parent_name")} placeholder="Your name" className="h-12 text-[16px] rounded-xl" />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-[16px]">Child name</Label>
                        <Input value={form.child_name} onChange={update("child_name")} placeholder="Child's name" className="h-12 text-[16px] rounded-xl" />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-[16px]">Email *</Label>
                        <Input type="email" value={form.email} onChange={update("email")} placeholder="you@example.com" className="h-12 text-[16px] rounded-xl" />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-[16px]">Phone *</Label>
                        <Input value={form.phone} onChange={update("phone")} placeholder="Your phone number" className="h-12 text-[16px] rounded-xl" data-testid="book-phone-input" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-[16px]">Anything we should know?</Label>
                      <Textarea value={form.notes} onChange={update("notes")} placeholder="Optional notes..." className="min-h-[90px] text-[16px] rounded-xl" />
                    </div>
                    <div className="rounded-2xl bg-violet-50/60 p-4 text-[15px] text-slate-600">
                      <span className="font-semibold text-slate-800">Summary:</span> {grade?.title} &middot; {fmtDate(date)} &middot; {slot}
                    </div>
                  </div>
                )}

                {/* Nav */}
                <div className="mt-8 flex items-center justify-between">
                  <Button variant="outline" onClick={back} disabled={step === 0} className="rounded-full h-12 px-5 text-[16px] border-violet-200 text-violet-700 hover:bg-violet-50 disabled:opacity-40">
                    <ArrowLeft className="mr-1.5 h-4 w-4" /> Back
                  </Button>
                  {step < 2 ? (
                    <Button onClick={next} className="cta-gradient text-white rounded-full h-12 px-6 text-[16px] font-semibold">
                      Continue <ArrowRight className="ml-1.5 h-4 w-4" />
                    </Button>
                  ) : (
                    <Button onClick={submit} disabled={loading} className="cta-gradient text-white rounded-full h-12 px-6 text-[16px] font-semibold">
                      {loading ? <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Booking...</> : <>Confirm Booking <Check className="ml-1.5 h-4 w-4" /></>}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
